/**
 * @file 登录引导浮窗
 * @author surer 2024-08-23
 */

import { FC, useEffect, useContext, useMemo, useState } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { StoreContext, namespace, ActionTypeValues } from '@/context';
import { PromiseState } from '@/constants';
import handleToUserCenter from '@/utils/userCenter';
import errorCode from '@/constants/errorCode';
import CommonIcon from '@/components/CommonIcon';

import Styles from './loginGuide.module.scss';

const ONE_DAY_TIMESTAMP = 24 * 60 * 60 * 1000;

export interface LoginGuideProps {
  className?: string;
  stateNamespace?: namespace.home | namespace.activity | namespace.square | namespace.consult | namespace.device | namespace.devices | namespace.profile;
}

export const LoginGuide: FC<LoginGuideProps> = ({ className, stateNamespace = namespace.home }) => {
  const { asPath } = useRouter();
  const { state } = useContext(StoreContext);
  const t = useTranslations();
  const { account, sensors } = state[namespace.app];
  const { track, TRACK_EVENTS } = sensors || {};

  const [isShowLoginGuide, setIsShowLoginGuide] = useState(false);

  const { id, uuid, status } = account || {};
  
  const isLogin = useMemo(() => {
    if (status !== PromiseState.PENDING) {
      if (id && uuid) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }, [id, uuid, status])

  
  useEffect(() => {
    if (status && status !== PromiseState.PENDING && !isLogin) {
      setTimeout(() => {
        const lastCloseTimestamp = localStorage.getItem('last-close-login-guide-timestamp');
        if (lastCloseTimestamp) {
          const timeSinceLastClose = Date.now() - parseInt(lastCloseTimestamp, 10);
          if (timeSinceLastClose > ONE_DAY_TIMESTAMP) {
            setIsShowLoginGuide(true);
            track?.(TRACK_EVENTS?.[stateNamespace].loginButtonShow.name, {position: 'Bottom'}, stateNamespace);
          }
        } else {
          setIsShowLoginGuide(true);
          track?.(TRACK_EVENTS?.[stateNamespace].loginButtonShow.name, {position: 'Bottom'}, stateNamespace);
        }
      }, 30000)
    }
  }, [track, status])

  useEffect(() => {
    if (isShowLoginGuide) {
      track?.(TRACK_EVENTS?.[stateNamespace].loginButtonShow.name, {position: 'Bottom'}, stateNamespace);
    }
  }, [asPath, track])


  const onClick = () => {
    track?.(TRACK_EVENTS?.[stateNamespace].loginButtonClick.name, {position: 'Bottom'}, stateNamespace);
    window.location.href = handleToUserCenter(errorCode.UNAUTHORIZED, window.location.href);
  }

  const onClose = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    localStorage.setItem('last-close-login-guide-timestamp', Date.now().toString());
    setIsShowLoginGuide(false);
    track?.(TRACK_EVENTS?.[stateNamespace].loginButtonCloseClick.name, {position: 'Bottom'}, stateNamespace);
  }

  return (
    <div className={cn(Styles['pc-root'], className, !isLogin && isShowLoginGuide ? Styles.fadeIn : Styles.fadeOut)} onClick={onClick}>
      {t('login_guide_title')}
      <CommonIcon icon="arrow-extend" className={Styles.arrowIcon} />
      <div className={Styles.close} onClick={onClose}>
        <CommonIcon icon="white-close" className={Styles.closeTcon} />
      </div>
    </div>
  )
}
